import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Paper,
  CircularProgress,
  Stack,
  Grid,
  alpha,
  Chip,
  Snackbar,
  Alert,
  MenuItem,
  Divider,
  Tooltip,
  Link,
  Rating,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  ArrowBack as BackIcon,
  FormatQuote as QuoteIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  ZoomIn as ZoomIcon,
  Image as ImageIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import {
  createTestimonial,
  updateTestimonial,
  getTestimonialById,
} from "../../../Services/Services";

// ─── Constants ───────────────────────────────────────────────────
import { brandColor as brand } from "../../../theme";

// ─── Shared style objects ─────────────────────────────────────────
const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    backgroundColor: "#f8fafc",
    transition: "all 0.2s ease",
    "& fieldset": { borderColor: "#e5e7eb" },
    "&:hover fieldset": { borderColor: "#c7d2fe" },
    "&.Mui-focused fieldset": { borderColor: brand, borderWidth: 1.5 },
  },
  "& .MuiInputLabel-root": { fontSize: "0.78rem", color: "#6b7280" },
  "& .MuiInputLabel-root.Mui-focused": { color: brand },
  "& .MuiFormHelperText-root": { fontSize: "0.72rem", marginLeft: "4px" },
};

const SectionTitle = ({ title, subtitle }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
    <Box sx={{ width: 3, height: 18, bgcolor: brand, borderRadius: 4 }} />
    <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "0.85rem" }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", ml: 0.5 }}>
        {subtitle}
      </Typography>
    )}
  </Stack>
);

const TestimonialForm = ({ editId = null, onSuccess, onCancel }) => {
  const fileRef = useRef(null);
  const isEdit = Boolean(editId);

  const [formData, setFormData] = useState({
    name: "",
    role: "",
    text: "",
    rating: 5,
    display_order: 0,
    is_active: true,
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState(null);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const [nameError, setNameError] = useState("");
  const [textError, setTextError] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (isEdit) fetchTestimonial();
  }, [editId]);

  const fetchTestimonial = async () => {
    setFetching(true);
    try {
      const data = await getTestimonialById(editId);
      if (data && data.id) {
        setFormData({
          name: data.name || "",
          role: data.role || "",
          text: data.text || "",
          rating: data.rating || 5,
          display_order: data.display_order || 0,
          is_active: data.is_active ?? true,
        });

        if (data.logo) {
          setLogoPreview(data.logo);
          setExistingLogoUrl(data.logo);
        }
      }
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to load testimonial data.",
        severity: "error",
      });
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "name") {
      setNameError(value.trim() ? "" : "Person name is required");
    }

    if (name === "text") {
      setTextError(value.trim() ? "" : "Testimonial text is required");
    }
  };

  const handleRatingChange = (_, newValue) => {
    setFormData((prev) => ({ ...prev, rating: newValue || 1 }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: "File size must be under 5MB.",
        severity: "error",
      });
      return;
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setSnackbar({
      open: true,
      message: "Photo uploaded successfully!",
      severity: "success",
    });
  };

  const removeLogo = () => {
    if (logoFile && existingLogoUrl) {
      setLogoFile(null);
      setLogoPreview(existingLogoUrl);
      if (fileRef.current) fileRef.current.value = "";
      setSnackbar({
        open: true,
        message: "Reverted to existing photo",
        severity: "info",
      });
      return;
    }

    setLogoFile(null);
    setLogoPreview(null);
    setExistingLogoUrl(null);
    if (fileRef.current) fileRef.current.value = "";
    setSnackbar({
      open: true,
      message: "Photo removed",
      severity: "info",
    });
  };

  const handleClearAll = () => {
    setFormData({
      name: "",
      role: "",
      text: "",
      rating: 5,
      display_order: 0,
      is_active: true,
    });
    setLogoFile(null);
    setLogoPreview(null);
    setExistingLogoUrl(null);
    setNameError("");
    setTextError("");
    if (fileRef.current) fileRef.current.value = "";

    setSnackbar({
      open: true,
      message: "All fields cleared!",
      severity: "info",
    });
  };

  const handleSubmit = async () => {
    const nameErr = formData.name.trim() ? "" : "Person name is required";
    const testimonialErr = formData.text.trim() ? "" : "Testimonial text is required";

    setNameError(nameErr);
    setTextError(testimonialErr);

    if (nameErr || testimonialErr) {
      setSnackbar({
        open: true,
        message: nameErr || testimonialErr,
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const payload = new FormData();
      payload.append("name", formData.name.trim());
      payload.append("role", formData.role.trim());
      payload.append("text", formData.text.trim());
      payload.append("rating", formData.rating);
      payload.append("display_order", formData.display_order);
      payload.append("is_active", formData.is_active);

      if (logoFile) {
        payload.append("logo", logoFile);
      }

      const result = isEdit
        ? await updateTestimonial(editId, payload)
        : await createTestimonial(payload);

      const data = result?.data ?? result;
      const explicitFailure =
        data?.success === false || data?.error || data?.status === "error";

      if (explicitFailure) {
        throw new Error(data?.message || data?.error || "Something went wrong");
      }

      setSnackbar({
        open: true,
        message: `Testimonial ${isEdit ? "updated" : "created"} successfully!`,
        severity: "success",
      });

      if (!isEdit) {
        setFormData({
          name: "",
          role: "",
          text: "",
          rating: 5,
          display_order: 0,
          is_active: true,
        });
        setLogoFile(null);
        setLogoPreview(null);
        setExistingLogoUrl(null);
        if (fileRef.current) fileRef.current.value = "";
      }

      if (onSuccess) onSuccess(data);
    } catch (error) {
      let msg = "Failed to save testimonial.";
      if (error?.response?.data?.message) msg = error.response.data.message;
      else if (error?.message) msg = error.message;

      setSnackbar({
        open: true,
        message: msg,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const isNewUpload = !!logoFile;
  const isExistingLogo = !logoFile && !!logoPreview && !!existingLogoUrl;
  const hasLogo = !!logoPreview;

  if (fetching) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress sx={{ color: brand }} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 0, pt: 0, mt: -2, maxWidth: 1200, mx: "auto", width: "100%" }}>
      <Paper
        sx={{
          borderRadius: "4px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
          overflow: "hidden",
          p: 0,
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            backgroundColor: "#f8fafc",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "4px",
                  bgcolor: alpha(brand, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <QuoteIcon sx={{ color: brand, fontSize: 18 }} />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#111827",
                    fontSize: "1rem",
                    lineHeight: 1.2,
                    textAlign: "left",
                  }}
                >
                  {isEdit ? "Edit Testimonial" : "Add New Testimonial"}
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "#6b7280", mt: 0.2 }}>
                  {isEdit
                    ? `Editing Testimonial ID: ${editId}`
                    : "Fill in the details below to create a testimonial"}
                </Typography>
              </Box>

              {isEdit && (
                <Chip
                  label={`ID: ${editId}`}
                  size="small"
                  sx={{
                    borderRadius: "4px",
                    bgcolor: alpha(brand, 0.1),
                    color: brand,
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    height: 20,
                  }}
                />
              )}
            </Stack>

            {onCancel && (
              <Button
                variant="outlined"
                startIcon={<BackIcon sx={{ fontSize: 16 }} />}
                onClick={onCancel}
                size="small"
                sx={{
                  borderRadius: "4px",
                  textTransform: "none",
                  fontSize: "0.75rem",
                  px: 2,
                  py: 0.75,
                  borderColor: "#d1d5db",
                  color: "#6b7280",
                  boxShadow: "none",
                  "&:hover": {
                    borderColor: brand,
                    color: brand,
                    bgcolor: alpha(brand, 0.04),
                    boxShadow: "none",
                  },
                }}
              >
                Back
              </Button>
            )}
          </Box>
        </Box>

        {/* ── Form Body ── */}
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2.5}>
            {/* ── Left Column ── */}
            <Grid item xs={12} md={8}>
              <SectionTitle title="Testimonial Details" />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    label="Person Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={(e) =>
                      setNameError(
                        e.target.value.trim() ? "" : "Person name is required"
                      )
                    }
                    error={!!nameError}
                    helperText={nameError}
                    placeholder="Enter person name"
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Role / Designation"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    placeholder="e.g. CEO, ABC Company"
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={5}
                    size="small"
                    label="Testimonial Text"
                    name="text"
                    value={formData.text}
                    onChange={handleChange}
                    onBlur={(e) =>
                      setTextError(
                        e.target.value.trim() ? "" : "Testimonial text is required"
                      )
                    }
                    error={!!textError}
                    helperText={textError || "What did the customer say?"}
                    placeholder="Write the testimonial here..."
                    sx={textFieldSx}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3, borderColor: "#e5e7eb" }} />

              <SectionTitle title="Rating & Display Settings" />

              <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      bgcolor: "#f8fafc",
                      px: 1.5,
                      py: 1.1,
                      minHeight: 68,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 0.5 }}
                    >
                      <Typography sx={{ fontSize: "0.78rem", color: "#6b7280" }}>
                        Rating
                      </Typography>
                      <Chip
                        label={`${formData.rating}/5`}
                        size="small"
                        sx={{
                          height: 18,
                          borderRadius: "4px",
                          bgcolor: alpha("#f59e0b", 0.12),
                          color: "#b45309",
                          fontSize: "0.62rem",
                          fontWeight: 700,
                        }}
                      />
                    </Stack>

                    <Rating
                      value={formData.rating}
                      onChange={handleRatingChange}
                      sx={{
                        "& .MuiRating-iconFilled": { color: "#f59e0b" },
                        "& .MuiRating-iconHover": { color: "#f59e0b" },
                      }}
                    />
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Display Order"
                    name="display_order"
                    value={formData.display_order}
                    onChange={handleChange}
                    helperText="Lower first"
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Status"
                    name="is_active"
                    value={formData.is_active ? "active" : "inactive"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_active: e.target.value === "active",
                      }))
                    }
                    helperText="Website visibility"
                    sx={textFieldSx}
                  >
                    <MenuItem value="active">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <ActiveIcon sx={{ fontSize: 16, color: "#10b981" }} />
                        <Typography sx={{ fontSize: "0.82rem" }}>Active</Typography>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="inactive">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <InactiveIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                        <Typography sx={{ fontSize: "0.82rem" }}>Inactive</Typography>
                      </Stack>
                    </MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3, borderColor: "#e5e7eb" }} />

              <SectionTitle
                title="Photo Upload"
                subtitle="(Max 5MB · PNG, JPG, WEBP accepted)"
              />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: "4px",
                      border: hasLogo
                        ? "1.5px solid #10b981"
                        : "1.5px dashed #d1d5db",
                      bgcolor: hasLogo ? alpha("#10b981", 0.04) : "#f8fafc",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {!hasLogo ? (
                      <Button
                        variant="text"
                        component="label"
                        fullWidth
                        startIcon={<UploadIcon sx={{ fontSize: 18 }} />}
                        sx={{
                          borderRadius: "4px",
                          textTransform: "none",
                          fontSize: "0.78rem",
                          color: brand,
                          py: "7.5px",
                          px: 1.5,
                          justifyContent: "flex-start",
                          bgcolor: "transparent",
                          boxShadow: "none",
                          "&:hover": {
                            bgcolor: alpha(brand, 0.06),
                            boxShadow: "none",
                          },
                        }}
                      >
                        Choose Photo
                        <input
                          type="file"
                          hidden
                          ref={fileRef}
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                      </Button>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 1,
                          py: "7.5px",
                        }}
                      >
                        <ImageIcon sx={{ color: brand, fontSize: 18 }} />

                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            fontSize: "0.78rem",
                            color: "#374151",
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {isNewUpload
                            ? logoFile.name
                            : existingLogoUrl
                            ? existingLogoUrl.split("/").pop()
                            : "Saved photo"}
                        </Typography>

                        {isNewUpload && (
                          <Chip
                            label="New"
                            size="small"
                            sx={{
                              borderRadius: "4px",
                              bgcolor: alpha(brand, 0.1),
                              color: brand,
                              fontSize: "0.6rem",
                              fontWeight: 600,
                              height: 18,
                              px: 0.5,
                            }}
                          />
                        )}

                        {isExistingLogo && existingLogoUrl && (
                          <Tooltip title="View photo">
                            <IconButton
                              size="small"
                              component={Link}
                              href={existingLogoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                color: brand,
                                borderRadius: "4px",
                                p: 0.4,
                                "&:hover": { bgcolor: alpha(brand, 0.08) },
                              }}
                            >
                              <ZoomIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {isNewUpload && (
                          <Tooltip title="Preview">
                            <IconButton
                              size="small"
                              onClick={() => setLightboxOpen(true)}
                              sx={{
                                color: brand,
                                borderRadius: "4px",
                                p: 0.4,
                                "&:hover": { bgcolor: alpha(brand, 0.08) },
                              }}
                            >
                              <ZoomIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}

                        <Tooltip title="Replace">
                          <IconButton
                            size="small"
                            component="label"
                            sx={{
                              color: "#6b7280",
                              borderRadius: "4px",
                              p: 0.4,
                              "&:hover": {
                                color: brand,
                                bgcolor: alpha(brand, 0.08),
                              },
                            }}
                          >
                            <UploadIcon sx={{ fontSize: 15 }} />
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={handleFileChange}
                            />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            onClick={removeLogo}
                            sx={{
                              color: "#dc2626",
                              borderRadius: "4px",
                              p: 0.4,
                              "&:hover": { bgcolor: alpha("#dc2626", 0.08) },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Grid>

            {/* ── Right Column: Live Preview ── */}
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "4px",
                  border: "1px solid #e5e7eb",
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 0.6,
                    bgcolor: "#f8fafc",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.8}>
                    <ImageIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                    <Typography
                      sx={{
                        fontSize: "0.68rem",
                        color: "#6b7280",
                        fontWeight: 500,
                      }}
                    >
                      Live Preview
                    </Typography>
                  </Stack>

                  {hasLogo && (
                    <IconButton
                      size="small"
                      onClick={() => setLightboxOpen(true)}
                      sx={{
                        p: 0.3,
                        borderRadius: "4px",
                        color: "#6b7280",
                        "&:hover": { color: brand, bgcolor: alpha(brand, 0.08) },
                      }}
                    >
                      <ZoomIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>

                {!hasLogo ? (
                  <Box
                    sx={{
                      height: 150,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      bgcolor: alpha(brand, 0.015),
                      backgroundImage:
                        "linear-gradient(45deg,#f3f4f6 25%,transparent 25%),linear-gradient(-45deg,#f3f4f6 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#f3f4f6 75%),linear-gradient(-45deg,transparent 75%,#f3f4f6 75%)",
                      backgroundSize: "16px 16px",
                      backgroundPosition: "0 0,0 8px,8px -8px,-8px 0px",
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: alpha(brand, 0.08),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 20, color: alpha(brand, 0.35) }} />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "#9ca3af",
                        fontWeight: 500,
                      }}
                    >
                      No photo uploaded yet
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    onClick={() => setLightboxOpen(true)}
                    sx={{
                      height: 150,
                      cursor: "zoom-in",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      component="img"
                      src={logoPreview}
                      alt="Preview"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        transition: "transform 0.25s ease",
                        "&:hover": { transform: "scale(1.04)" },
                      }}
                    />
                  </Box>
                )}

                <Box sx={{ p: 2 }}>
                  <Rating
                    value={formData.rating}
                    readOnly
                    size="small"
                    sx={{
                      mb: 1,
                      "& .MuiRating-iconFilled": { color: "#f59e0b" },
                    }}
                  />

                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: "#374151",
                      lineHeight: 1.55,
                      minHeight: 72,
                      display: "-webkit-box",
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {formData.text?.trim()
                      ? `“${formData.text}”`
                      : "The testimonial preview text will appear here as you type."}
                  </Typography>

                  <Divider sx={{ my: 1.5, borderColor: "#eef0f4" }} />

                  <Typography
                    sx={{
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      color: "#111827",
                    }}
                  >
                    {formData.name?.trim() || "Person Name"}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      color: "#6b7280",
                      mt: 0.3,
                    }}
                  >
                    {formData.role?.trim() || "Role / Designation"}
                  </Typography>

                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Chip
                      label={formData.is_active ? "Active" : "Inactive"}
                      size="small"
                      sx={{
                        borderRadius: "4px",
                        height: 20,
                        fontSize: "0.62rem",
                        fontWeight: 600,
                        bgcolor: formData.is_active
                          ? alpha("#10b981", 0.12)
                          : alpha("#ef4444", 0.12),
                        color: formData.is_active ? "#10b981" : "#ef4444",
                      }}
                    />
                    <Chip
                      label={`Order: ${formData.display_order}`}
                      size="small"
                      sx={{
                        borderRadius: "4px",
                        height: 20,
                        fontSize: "0.62rem",
                        fontWeight: 600,
                        bgcolor: alpha(brand, 0.08),
                        color: brand,
                      }}
                    />
                  </Stack>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* ── Action Buttons ── */}
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="flex-end"
            sx={{ mt: 3 }}
          >
            <Button
              startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
              onClick={handleClearAll}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "0.75rem",
                px: 2.5,
                py: 0.9,
                bgcolor: alpha("#dc2626", 0.07),
                color: "#dc2626",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: alpha("#dc2626", 0.14),
                  boxShadow: "none",
                },
              }}
            >
              Clear All
            </Button>

            <Button
              variant="contained"
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : (
                  <SaveIcon sx={{ fontSize: 16 }} />
                )
              }
              onClick={handleSubmit}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "0.75rem",
                px: 2.5,
                py: 0.9,
                bgcolor: brand,
                boxShadow: "none",
                "&:hover": { bgcolor: "#4338ca", boxShadow: "none" },
                "&.Mui-disabled": {
                  bgcolor: alpha(brand, 0.5),
                  color: "#fff",
                },
              }}
            >
              {loading
                ? "Saving…"
                : isEdit
                ? "Update Testimonial"
                : "Add Testimonial"}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* ── Lightbox ── */}
      {lightboxOpen && logoPreview && (
        <Box
          onClick={() => setLightboxOpen(false)}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            bgcolor: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
            backdropFilter: "blur(4px)",
          }}
        >
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              bgcolor: "rgba(255,255,255,0.1)",
              color: "#fff",
              borderRadius: "4px",
              "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Chip
            label={
              logoFile
                ? `${logoFile.name} — ${(logoFile.size / 1024).toFixed(1)} KB`
                : "Photo Preview"
            }
            size="small"
            sx={{
              position: "absolute",
              bottom: 20,
              bgcolor: "rgba(255,255,255,0.12)",
              color: "#fff",
              fontSize: "0.72rem",
              borderRadius: "4px",
              height: 24,
            }}
          />

          <Box
            component="img"
            src={logoPreview}
            alt="Full preview"
            onClick={(e) => e.stopPropagation()}
            sx={{
              maxWidth: "85vw",
              maxHeight: "85vh",
              objectFit: "contain",
              borderRadius: "4px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              cursor: "default",
              bgcolor: "#fff",
              p: 2,
            }}
          />
        </Box>
      )}

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
    top: "80px !important",
  }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{
            borderRadius: "4px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TestimonialForm;